import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Modal,
  MultiSelect,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertCircleFilled,
  IconArrowLeft,
  IconDeviceDesktop,
} from '@tabler/icons-react';

import { useAuthContext, useNetworkScans, useTags } from '../../hooks';
import { Pagan, Tag } from '../../hooks/api/types';

export const ScanDetail = () => {
  const { scanId } = useParams<{ scanId: string }>();
  const navigate = useNavigate();
  const { token, isAuthenticated, logout } = useAuthContext();

  const { pagans, loading, error, fetchPagans, convertPaganToDuck } =
    useNetworkScans({
      token: token || undefined,
      onAuthError: logout,
    });

  const { fetchTags } = useTags({
    token: token || undefined,
    onAuthError: logout,
  });

  const [convertModalOpened, setConvertModalOpened] = useState(false);
  const [selectedPagan, setSelectedPagan] = useState<Pagan | null>(null);
  const [duckName, setDuckName] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);

  useEffect(() => {
    if (isAuthenticated && token && availableTags.length === 0) {
      console.log('fetched tags', isAuthenticated, token);
      fetchTags().then((tags) => {
        setAvailableTags(tags.results);
      });
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (isAuthenticated && token && scanId) {
      fetchPagans(parseInt(scanId));
    }
  }, [isAuthenticated, token, scanId]);

  const handleConvertToDuck = async () => {
    if (!selectedPagan || !duckName || selectedTags.length === 0) {
      return;
    }

    try {
      const tagIds = selectedTags
        .map((tagName) => {
          const tag = availableTags.find((t) => t.name === tagName);
          return tag?.id;
        })
        .filter((id) => id !== undefined) as number[];

      await convertPaganToDuck(selectedPagan.id, duckName, tagIds);
      setConvertModalOpened(false);
      setSelectedPagan(null);
      setDuckName('');
      setSelectedTags([]);
      // Refresh the pagans list
      fetchPagans(parseInt(scanId!));
    } catch (error) {
      console.error('Failed to convert pagan to duck:', error);
    }
  };

  const openConvertModal = (pagan: Pagan) => {
    setSelectedPagan(pagan);
    setConvertModalOpened(true);
  };

  const canConvert = (pagan: Pagan) => {
    return !pagan.is_random && !pagan.is_private;
  };

  if (loading) {
    return <div>Loading scan details...</div>;
  }

  if (error) {
    return <div>Error loading scan details: {error.message}</div>;
  }

  return (
    <Container>
      <Stack>
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Title order={1}>Scan #{scanId} - Unknown Devices</Title>
        </Group>

        {pagans.length === 0 ? (
          <Alert
            variant="light"
            color="blue"
            title="No unknown devices found"
            icon={<IconAlertCircleFilled />}
          >
            This scan didn't find any unknown devices on the network.
          </Alert>
        ) : (
          <SimpleGrid cols={2} spacing="md">
            {pagans.map((pagan) => (
              <Card
                key={pagan.id}
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
              >
                <Group justify="space-between" mb="xs">
                  <Text fw={500}>
                    <IconDeviceDesktop size={16} style={{ marginRight: 8 }} />
                    {pagan.ip_address}
                  </Text>
                  <Group>
                    {pagan.is_random && (
                      <Badge color="orange">Random MAC</Badge>
                    )}
                    {pagan.is_private && <Badge color="red">Private MAC</Badge>}
                  </Group>
                </Group>

                <Text size="sm" mb="xs">
                  MAC: {pagan.mac_address || 'Unknown'}
                </Text>

                {pagan.mac_vendor && (
                  <Text size="sm" mb="xs">
                    Vendor: {pagan.mac_vendor}
                  </Text>
                )}

                <Text size="sm" mb="md" c="dimmed">
                  Found: {new Date(pagan.created_at).toLocaleString()}
                </Text>

                <Button
                  fullWidth
                  variant={canConvert(pagan) ? 'filled' : 'outline'}
                  disabled={!canConvert(pagan)}
                  onClick={() => openConvertModal(pagan)}
                >
                  {canConvert(pagan) ? 'Convert to Duck' : 'Cannot Convert'}
                </Button>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>

      <Modal
        opened={convertModalOpened}
        onClose={() => setConvertModalOpened(false)}
        title="Convert to Duck"
        size="md"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            Converting device: {selectedPagan?.ip_address} (
            {selectedPagan?.mac_address})
          </Text>

          <TextInput
            label="Duck Name"
            placeholder="Enter a name for this device"
            value={duckName}
            onChange={(e) => setDuckName(e.target.value)}
            required
          />

          <MultiSelect
            label="Tags"
            placeholder="Select tags for this device"
            data={availableTags.map((tag) => ({
              value: tag.name,
              label: tag.name,
            }))}
            value={selectedTags}
            onChange={setSelectedTags}
            required
          />

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => setConvertModalOpened(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConvertToDuck}
              disabled={!duckName || selectedTags.length === 0}
            >
              Convert
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};
